import { useTextMatchingApi } from '@/api/ai';
import { Button } from '@/components/ui/button';
import { FlexCol } from '@/components/ui/flex';
import { useDownloadSourceFileService } from '@/services/sources';
import { Viewer } from '@react-pdf-viewer/core';
import { defaultLayoutPlugin } from '@react-pdf-viewer/default-layout';
import {
  highlightPlugin,
  type HighlightArea,
} from '@react-pdf-viewer/highlight';
import * as pdfjsLib from 'pdfjs-dist';

// Import CSS for default layout and highlight plugin
import '@react-pdf-viewer/default-layout/lib/styles/index.css';
import '@react-pdf-viewer/highlight/lib/styles/index.css';
import { Loader2, X } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState, type FC } from 'react';

// Import CSS for react-pdf-viewer
import '@react-pdf-viewer/core/lib/styles/index.css';
import '@react-pdf-viewer/search/lib/styles/index.css';
import { useChatCtx } from './ChatContext';

// Text item with position information from PDF.js
interface TextItem {
  str: string;
  dir: string;
  width: number;
  height: number;
  transform: number[]; // [a, b, c, d, e, f] transformation matrix
  fontName: string;
  hasEOL: boolean;
}

// Text position information
interface TextPosition {
  x: number;
  y: number;
  width: number;
  height: number;
  text: string;
  charIndex: number; // Character index in the full text
}

// Calculate precise text positions using PDF.js coordinates
const calculateTextPositions = (textContent: any): TextPosition[] => {
  const positions: TextPosition[] = [];
  let globalCharIndex = 0;

  textContent.items.forEach((item: TextItem) => {
    // Extract position from transformation matrix
    // transform[4] = x position, transform[5] = y position
    const x = item.transform[4];
    const y = item.transform[5];

    // Calculate position for each character in the text item
    const chars = item.str.split('');
    const charWidth = item.width / chars.length; // Approximate character width

    chars.forEach((char, charIdx) => {
      positions.push({
        x: x + charIdx * charWidth,
        y,
        width: charWidth,
        height: item.height,
        text: char,
        charIndex: globalCharIndex + charIdx,
      });
    });

    globalCharIndex += item.str.length;
  });

  return positions;
};

// Find text span positions for highlighting
const findTextSpanPositions = (
  textPositions: TextPosition[],
  targetText: string,
  fullText: string,
  pageWidth: number,
  pageHeight: number
): HighlightArea[] => {
  const targetStart = fullText.toLowerCase().indexOf(targetText.toLowerCase());
  if (targetStart === -1) return [];

  const targetEnd = targetStart + targetText.length;

  // Find all text positions that fall within the target range
  const matchingPositions = textPositions.filter(
    pos => pos.charIndex >= targetStart && pos.charIndex < targetEnd
  );

  if (matchingPositions.length === 0) return [];

  // Group consecutive positions into line segments
  const lineSegments: TextPosition[][] = [];
  let currentSegment: TextPosition[] = [];

  matchingPositions.forEach((pos, idx) => {
    if (idx === 0) {
      currentSegment = [pos];
    } else {
      const prevPos = matchingPositions[idx - 1];
      const yDiff = Math.abs(pos.y - prevPos.y);

      // If Y position changes significantly, start a new line
      if (yDiff > pos.height * 0.5) {
        lineSegments.push(currentSegment);
        currentSegment = [pos];
      } else {
        currentSegment.push(pos);
      }
    }

    // Add the last segment
    if (idx === matchingPositions.length - 1) {
      lineSegments.push(currentSegment);
    }
  });

  // Convert line segments to highlight areas
  return lineSegments.map((segment, segmentIndex) => {
    // Get the Y position of this line segment
    const segmentY = segment[0].y;

    // Find all text positions on the same line (same Y position within tolerance)
    const linePositions = textPositions.filter(
      pos => Math.abs(pos.y - segmentY) <= segment[0].height * 0.3
    );

    // For first and last segments, extend to full line width
    let left, right;

    if (segmentIndex === 0 || segmentIndex === lineSegments.length - 1) {
      // Extend to full line for first/last segments
      left = Math.min(...linePositions.map(p => p.x));
      right = Math.max(...linePositions.map(p => p.x + p.width));
    } else {
      // Keep original segment boundaries for middle segments
      left = Math.min(...segment.map(p => p.x));
      right = Math.max(...segment.map(p => p.x + p.width));
    }

    const top = Math.min(...segment.map(p => p.y));
    const bottom = Math.max(...segment.map(p => p.y + p.height));

    // Convert PDF coordinates to percentages
    // Note: PDF coordinate system has (0,0) at bottom-left, need to flip Y
    const leftPercent = (left / pageWidth) * 100;
    const topPercent = ((pageHeight - bottom) / pageHeight) * 100; // Flip Y coordinate
    const widthPercent = ((right - left) / pageWidth) * 100;
    const heightPercent = ((bottom - top) / pageHeight) * 100;

    return {
      pageIndex: 0, // Will be set correctly by caller
      left: leftPercent,
      top: topPercent,
      width: widthPercent,
      height: heightPercent,
    };
  });
};

export const DocumentPreview: FC = () => {
  const { displayedFile, setDisplayedFile } = useChatCtx();
  const [highlightAreas, setHighlightAreas] = useState<
    (HighlightArea & { id: string })[]
  >([]);
  const [isProcessingHighlight, setIsProcessingHighlight] = useState(false);

  const textMatchingMutation = useTextMatchingApi();

  const { sourceFile, isPending: isSourceFilePending } =
    useDownloadSourceFileService(displayedFile?.fileId);

  const fileUrl = useMemo(() => {
    if (displayedFile && sourceFile) {
      // Convert Blob to URL for react-pdf-viewer
      if (sourceFile instanceof Blob) {
        return URL.createObjectURL(sourceFile);
      }
      return sourceFile;
    }
    return undefined;
  }, [displayedFile, sourceFile]);

  // Extract text from PDF for better matching
  const extractPdfText = useCallback(
    async (pdfUrl: string) => {
      try {
        setIsProcessingHighlight(true);
        const pdf = await pdfjsLib.getDocument(pdfUrl).promise;

        // Extract text from only the specific page
        const pageNumber = displayedFile?.pageNumber || 1;

        if (pageNumber > pdf.numPages) {
          console.warn(
            `Page ${pageNumber} does not exist in PDF with ${pdf.numPages} pages`
          );
          return;
        }

        const page = await pdf.getPage(pageNumber);
        const viewport = page.getViewport({ scale: 1.0 });
        const textContent = await page.getTextContent();

        const pageText = textContent.items
          .map((item: any) => item.str)
          .join(' ');

        // If we have quoted text, first check for exact match, then use AI
        if (displayedFile?.quotedTexts) {
          const allHighlightAreas: (HighlightArea & { id: string })[] = [];

          try {
            for (
              let quoteIdx = 0;
              quoteIdx < displayedFile.quotedTexts.length;
              quoteIdx++
            ) {
              const quotedText = displayedFile.quotedTexts[quoteIdx];
              let matchResult: string | null = null;

              // First, try exact string match (case-insensitive)
              const normalizedQuotedText = quotedText.toLowerCase().trim();
              const normalizedPageText = pageText.toLowerCase();

              if (normalizedPageText.includes(normalizedQuotedText)) {
                // Find the exact match with original casing
                const startIndex =
                  normalizedPageText.indexOf(normalizedQuotedText);
                matchResult = pageText.substring(
                  startIndex,
                  startIndex + quotedText.length
                );
              } else {
                // Use AI as fallback for semantic matching
                const response = await textMatchingMutation.mutateAsync({
                  needle: quotedText,
                  haystack: pageText,
                });

                matchResult = response.match;
              }

              const targetText = matchResult || quotedText;

              // Calculate precise text positions
              const textPositions = calculateTextPositions(textContent);
              const preciseAreas = findTextSpanPositions(
                textPositions,
                targetText,
                pageText,
                viewport.width,
                viewport.height
              );

              // Collect highlight areas for this quoted text
              // Use quoteIdx to ensure uniqueness even for duplicate quoted texts
              const areasWithPageIndex = preciseAreas.map((area, areaIdx) => ({
                ...area,
                pageIndex: pageNumber - 1, // Convert to 0-based index
                id: `highlight-${pageNumber}-q${quoteIdx}-a${areaIdx}`,
              }));

              allHighlightAreas.push(...areasWithPageIndex);
            }

            // Set all highlight areas at once
            if (allHighlightAreas.length > 0) {
              setHighlightAreas(allHighlightAreas);
            }
          } catch (error) {
            // Fallback to original text
            const fallbackAreas: (HighlightArea & { id: string })[] = [];

            for (
              let quoteIdx = 0;
              quoteIdx < displayedFile.quotedTexts.length;
              quoteIdx++
            ) {
              const quotedText = displayedFile.quotedTexts[quoteIdx];
              const textPositions = calculateTextPositions(textContent);
              const preciseAreas = findTextSpanPositions(
                textPositions,
                quotedText,
                pageText,
                viewport.width,
                viewport.height
              );

              // Use quoteIdx to ensure uniqueness even for duplicate quoted texts
              const areasWithPageIndex = preciseAreas.map((area, areaIdx) => ({
                ...area,
                pageIndex: pageNumber - 1,
                id: `highlight-${pageNumber}-q${quoteIdx}-a${areaIdx}`,
              }));

              fallbackAreas.push(...areasWithPageIndex);
            }

            if (fallbackAreas.length > 0) {
              setHighlightAreas(fallbackAreas);
            }
          }
        }
      } catch (error) {
        console.error('Error extracting PDF text:', error);
      } finally {
        setIsProcessingHighlight(false);
      }
    },
    [displayedFile?.pageNumber, displayedFile?.quotedTexts]
  );

  // Extract PDF text when file URL changes
  useEffect(() => {
    if (fileUrl && displayedFile?.quotedTexts) {
      extractPdfText(fileUrl);
    }
  }, [fileUrl, extractPdfText]);

  // Create plugin instances at the top level (Rules of Hooks compliant)
  const highlightPluginInstance = highlightPlugin({
    renderHighlights: props => (
      <div>
        {highlightAreas
          .filter(area => area.pageIndex === props.pageIndex)
          .map(area => (
            <div
              key={area.id}
              className="absolute bg-yellow-400 opacity-50 pointer-events-none"
              style={{
                left: `${area.left}%`,
                top: `${area.top}%`,
                height: `${area.height}%`,
                width: `${area.width}%`,
              }}
            />
          ))}
      </div>
    ),
  });

  const defaultLayoutPluginInstance = defaultLayoutPlugin();

  const plugins = useMemo(
    () => [highlightPluginInstance, defaultLayoutPluginInstance],
    [highlightPluginInstance, defaultLayoutPluginInstance]
  );

  const handleClose = () => {
    setDisplayedFile(undefined);
  };

  // Cleanup URL object when component unmounts or file changes
  useEffect(() => {
    return () => {
      if (fileUrl && fileUrl.startsWith('blob:')) {
        URL.revokeObjectURL(fileUrl);
      }
    };
  }, [fileUrl]);

  if (!displayedFile || isSourceFilePending || !fileUrl) return null;

  // Show loader while processing highlight
  if (displayedFile.quotedTexts && isProcessingHighlight) {
    return (
      <FlexCol
        alignItems="center"
        justifyContent="center"
        className="w-1/2 shrink-0 h-full max-h-full relative group border-r border-tertiary"
      >
        {/* Close button */}
        <Button
          variant="outline"
          size="icon"
          className="absolute top-2 right-2 z-10"
          onClick={handleClose}
        >
          <X />
        </Button>

        {/* Loader */}
        <FlexCol alignItems="center" justifyContent="center" gap="3">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </FlexCol>
      </FlexCol>
    );
  }

  return (
    <FlexCol
      alignItems="center"
      className="w-1/2 shrink-0 h-full max-h-full relative group border-r border-tertiary"
    >
      {/* Close button */}
      <Button
        variant="outline"
        size="icon"
        className="absolute top-2 right-2 z-10 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
        onClick={handleClose}
      >
        <X />
      </Button>

      {/* PDF Viewer */}
      <div className="w-full h-full">
        <Viewer
          fileUrl={fileUrl}
          plugins={plugins}
          defaultScale={1}
          initialPage={
            displayedFile.pageNumber ? displayedFile.pageNumber - 1 : 0
          }
        />
      </div>
    </FlexCol>
  );
};
